import { catchBackendApiClient } from '@/client/customFetcher';
import authService from '@/services/auth.service';
import envService from '@/services/env.service';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ValidationResults {
  overall_passed: boolean;
  judges: Record<string, unknown>;
  critical_failures: string[];
  timestamp: string;
  validation_duration_ms: number;
}

type LetterStatus =
  | 'idle'
  | 'streaming'
  | 'validating'
  | 'validation_failed'
  | 'validation_passed'
  | 'cache_hit'
  | 'error';

interface LetterGenerationState {
  status: LetterStatus;
  content: string;
  letter_id: string | null;
  attempt: number;
  validation_results: ValidationResults | null;
  error: string | null;
  isDownloading: boolean;
}

interface UseLetterGenerationReturn extends LetterGenerationState {
  startGeneration: () => Promise<void>;
  retry: () => Promise<void>;
  downloadPDF: () => Promise<void>;
  reset: () => void;
  clearStatus: () => void;
  setLetterId: (letterId: string) => void;
  isGenerating: boolean;
  canDownload: boolean;
  canRetry: boolean;
}

const API_BASE_URL = envService.getEnv().VITE_CATCH_BACKEND_API_URL;

export const useLetterGeneration = (): UseLetterGenerationReturn => {
  const [state, setState] = useState<LetterGenerationState>({
    status: 'idle',
    content: '',
    letter_id: null,
    attempt: 0,
    validation_results: null,
    error: null,
    isDownloading: false,
  });

  const streamReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const workflowIdRef = useRef<string | null>(null);

  // Cleanup SSE connection
  const closeSSE = useCallback(async () => {
    if (streamReaderRef.current) {
      try {
        await streamReaderRef.current.cancel();
      } catch (err) {
        console.warn('Error canceling stream:', err);
      }
      streamReaderRef.current = null;
    }
  }, []);

  // Start generation flow
  const startGeneration = useCallback(async () => {
    console.log('[useLetterGeneration] startGeneration called');
    try {
      // Close any existing connection
      closeSSE();

      // Reset state
      setState({
        status: 'streaming',
        content: '',
        letter_id: null,
        attempt: 1,
        validation_results: null,
        error: null,
        isDownloading: false,
      });

      // Step 1: POST to start workflow
      console.log('[useLetterGeneration] POSTing to', `${API_BASE_URL}/v1/llm-context/generate-letter`);
      const response = await catchBackendApiClient.post<{ workflow_id: string }>(
        '/v1/llm-context/generate-letter'
      );

      console.log('[useLetterGeneration] POST response:', response.status, response.data);

      const { workflow_id } = response.data;
      console.log('[useLetterGeneration] Got workflow_id:', workflow_id);
      workflowIdRef.current = workflow_id;

      // Step 2: Stream using fetch with proper auth headers (EventSource doesn't support headers)
      const streamUrl = `${API_BASE_URL}/v1/llm-context/stream/${workflow_id}`;
      console.log('[useLetterGeneration] Connecting to SSE stream:', streamUrl);

      const token = authService.getToken();
      const streamResponse = await fetch(streamUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        credentials: 'include',
      });

      if (!streamResponse.ok) {
        throw new Error(`Stream connection failed: ${streamResponse.status}`);
      }

      const reader = streamResponse.body?.getReader();
      if (!reader) {
        throw new Error('Stream reader not available');
      }

      // Store reader for cleanup
      streamReaderRef.current = reader;

      const decoder = new TextDecoder();
      let buffer = '';

      // Read stream
      const readStream = async () => {
        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log('[useLetterGeneration] Stream complete');
              break;
            }

            // Decode chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE messages (ending with \n\n)
            const messages = buffer.split('\n\n');
            buffer = messages.pop() || ''; // Keep incomplete message in buffer

            for (const message of messages) {
              if (message.startsWith('data: ')) {
                const jsonData = message.slice(6); // Remove 'data: ' prefix
                try {
                  const data = JSON.parse(jsonData);

                  // Map backend workflow state to frontend state
                  switch (data.status) {
                    case 'streaming':
                      setState((prev) => ({
                        ...prev,
                        status: 'streaming',
                        content: data.content || '',
                      }));
                      break;

                    case 'validating':
                    case 'saving':
                      // Conflate validating and saving to avoid flash - both show "Validating your letter"
                      setState((prev) => ({
                        ...prev,
                        status: 'validating',
                        content: data.content || prev.content,
                      }));
                      break;

                    case 'retry':
                      setState((prev) => ({
                        ...prev,
                        status: 'validation_failed',
                        validation_results: data.validation_results || null,
                        attempt: data.attempt || prev.attempt,
                        content: '', // Clear content on retry - typewriter will reset
                      }));
                      break;

                    case 'completed':
                      setState((prev) => ({
                        ...prev,
                        status: data.letter_id ? 'validation_passed' : 'cache_hit',
                        letter_id: data.letter_id || null,
                        content: data.content || prev.content,
                      }));
                      await reader.cancel();
                      return;

                    case 'failed':
                    case 'error':
                      setState((prev) => ({
                        ...prev,
                        status: 'error',
                        error: data.error || 'Generation failed',
                      }));
                      await reader.cancel();
                      return;

                    default:
                      console.warn('Unknown SSE status:', data.status);
                  }
                } catch (err) {
                  console.error('Failed to parse SSE event:', err, jsonData);
                }
              }
            }
          }
        } catch (err) {
          console.error('Stream reading error:', err);
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: err instanceof Error ? err.message : 'Stream error',
          }));
        }
      };

      readStream();
    } catch (err) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: err instanceof Error ? err.message : 'Unknown error',
      }));
      closeSSE();
    }
  }, [closeSSE]);

  // Retry generation
  const retry = useCallback(async () => {
    await startGeneration();
  }, [startGeneration]);

  // Download PDF
  const downloadPDF = useCallback(async () => {
    if (!state.letter_id) {
      console.error('No letter_id available for download');
      return;
    }

    setState((prev) => ({ ...prev, isDownloading: true }));

    try {
      const response = await catchBackendApiClient.get(
        `/v1/llm-context/download/${state.letter_id}`,
        {
          responseType: 'blob',
        }
      );

      if (response.status === 501) {
        // PDF generation not implemented yet
        alert('PDF download coming soon!');
        setState((prev) => ({ ...prev, isDownloading: false }));
        return;
      }

      // Trigger browser download
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `letter-to-doctor-${state.letter_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setState((prev) => ({ ...prev, isDownloading: false }));
    } catch (err) {
      console.error('PDF download error:', err);
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Download failed',
        isDownloading: false,
      }));
    }
  }, [state.letter_id]);

  // Reset to idle state
  const reset = useCallback(() => {
    closeSSE();
    setState({
      status: 'idle',
      content: '',
      letter_id: null,
      attempt: 0,
      validation_results: null,
      error: null,
      isDownloading: false,
    });
  }, [closeSSE]);

  // Set letter ID (for loading from localStorage)
  const setLetterId = useCallback((letterId: string) => {
    setState((prev) => ({
      ...prev,
      letter_id: letterId,
    }));
  }, []);

  // Clear status without resetting letter_id (for closing modal after successful generation)
  const clearStatus = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: 'idle',
      error: null,
    }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closeSSE();
    };
  }, [closeSSE]);

  // Helper flags
  const isGenerating = state.status === 'streaming' || state.status === 'validating';
  const canDownload = state.status === 'validation_passed' && state.letter_id !== null;
  const canRetry = state.status === 'validation_failed' || state.status === 'error';

  return {
    ...state,
    startGeneration,
    retry,
    downloadPDF,
    reset,
    clearStatus,
    setLetterId,
    isGenerating,
    canDownload,
    canRetry,
  };
};
